import {ChangeDetectionStrategy, Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {AdminService} from "../../_services/admin.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {DatePipe} from "@angular/common";
import {ConfirmDialogComponent} from "../../shared/confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../../_types/shared";
import {UserExtended} from "../../_types/user";
import {ModifiedPermission} from "../users/user/user.component";

interface UploadSessionFormData {
    user: number,
    permission: ModifiedPermission
}

@Component({
    selector: 'app-upload-session-form',
    templateUrl: './upload-session-form.component.html',
    styleUrl: './upload-session-form.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UploadSessionFormComponent {
    user: UserExtended | undefined;
    sessionForm: FormGroup;
    loading: boolean = true;
    patch: boolean = false;
    timelineAccessId: number | undefined

    constructor(@Inject(MAT_DIALOG_DATA) protected data: UploadSessionFormData,
                private snackbarService: SnackbarService,
                private formBuilder: FormBuilder,
                protected dialogRef: MatDialogRef<UploadSessionFormComponent>,
                private adminService: AdminService,
                public dialog: MatDialog) {

        this.sessionForm = this.formBuilder.group({
            start_date: [new Date(), Validators.required],
            period: ['annually', Validators.required],
            grace_period: [false],
            image_limit_default: [0],
            video_limit_default: [0],
            audio_limit_default: [0],
            document_limit_default: [0],
            message_limit_default: [0],
        });

        if (this.data.permission?.id) {
            this.timelineAccessId = this.data.permission?.id
            this.adminService.getUploadSessionSettings(this.data.permission?.id).subscribe({
                next: r => {
                    if (r) {
                        this.patch = true;
                        this.sessionForm.patchValue({
                            start_date: r.start_date,
                            period: r.period,
                            image_limit_default: r.image_limit_default,
                            video_limit_default: r.video_limit_default,
                            audio_limit_default: r.audio_limit_default,
                            document_limit_default: r.document_limit_default,
                            message_limit_default: r.message_limit_default,
                        })
                    }
                }, error: e => {
                    let error = e.detail || e
                    if (error != 'No UserUploadFrequencyLimit matches the given query.') {
                        this.snackbarService.openSnackBar(error, 'error')
                    }
                    console.error(error)
                }
            });
        }
        this.adminService.getUser(this.data.user).subscribe({
            next: r => {
                this.user = r;
                this.loading = false;
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
                this.loading = false;
            }
        });
    }

    saveSession(): void {
        let data: any = this.sessionForm.value;
        data.start_date = new DatePipe('en-GB').transform(data.start_date, 'yyyy-MM-dd');
        if (data.grace_period) {
            data.settle_date = data.start_date;
        }
        delete data['grace_period'];
        data['image_limit_remaining'] = data.image_limit_default;
        data['video_limit_remaining'] = data.video_limit_default;
        data['audio_limit_remaining'] = data.audio_limit_default;
        data['document_limit_remaining'] = data.document_limit_default;
        data['message_limit_remaining'] = data.message_limit_default;

        if (this.timelineAccessId) {
            if (this.patch) {
                this.adminService.editUploadSessionSettings(this.timelineAccessId, data).subscribe({
                    next: r => {
                        this.snackbarService.openSnackBar('Upload Session Updated Successfully', 'success');
                        this.dialogRef.close();
                    }, error: error => {
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(error)
                    }
                });
            } else {
                this.adminService.createUploadSessionSettings(this.timelineAccessId, data).subscribe({
                    next: r => {
                        this.snackbarService.openSnackBar('Upload Session Created Successfully', 'success');
                        this.dialogRef.close();
                    }, error: error => {
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(error)
                    }
                });
            }
        } else {
            this.snackbarService.openSnackBar('No timeline_access_id present', 'error');
        }
    }

    deleteSession(): void {
        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this Upload Frequency? Deleting this will allow the user to upload unlimited times.',
                title: 'Delete Upload Frequency',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe((resp): void => {
            if (resp) {
                this.adminService.deleteUploadSessionSettings(this.data.permission?.id).subscribe({
                    next: r => {
                        this.snackbarService.openSnackBar('Upload Session Deleted Successfully', 'success');
                        this.dialogRef.close();
                    }, error: e => {
                        let error: string = e.detail || e
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(e)
                    }
                });
            }
        });
    }
}
